import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    IconButton,
    useTheme,
    alpha,
    CircularProgress,
    Backdrop
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useForm, Controller } from 'react-hook-form';
import { leadGeneration, notify, OtpVerify, verifyUser } from '../../services/advisorLeadService';
import { showToast } from '../../components/common/Util/Util';
import { useSelector } from 'react-redux';
import { ip, coordinates } from '../../Store/userSlice';

const TalkToAdvisorLeadForm = ({ open, onClose, onSuccess }) => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [otp, setOtp] = useState('');
    const [customerNum, setCustomerNum] = useState('');
    const [sendOtpDisabled, setSendOtpDisabled] = useState(false);
    const [verifyOtpDisabled, setVerifyOtpDisabled] = useState(false);
    const IpAddress = useSelector(ip);
    const userCoordinates = useSelector(coordinates);
    const {
        control,
        handleSubmit,
        formState: { errors },
        reset
    } = useForm();

    const handleClose = () => {
        reset()
        setOtpSent(false);
        setOtp('');
        setCustomerNum('');
        setSendOtpDisabled(false);
        setVerifyOtpDisabled(false);
        onClose();
    };

    const handleLeadGeneration = () => {
        if (!customerNum) {
            showToast('Please enter a valid mobile number', 'error');
            return;
        }

        if (otp === '') {
            showToast('Please enter the OTP', 'error');
            return;
        } else if (otp?.length !== 4) {
            showToast('Please enter the complete OTP', 'error');
            return;
        }

        const verifyOtpRequest = {
            mobileNumber: customerNum?.replace(/-/g, ''),
            mobileOtp: otp
        };

        setLoading(true);
        setVerifyOtpDisabled(true);
        OtpVerify(verifyOtpRequest)
            .then((res) => {
                if (res?.statusCode === 200 && res?.message === "OTP Verified") {
                    const leadRequest = {
                        mobileNumber: customerNum?.replace(/-/g, ''),
                        leadType: 'advisor',
                        ip: IpAddress,
                        longitude: userCoordinates?.lng || '',
                        latitude: userCoordinates?.lat || '',
                        source: 'admin'
                    };
                    leadGeneration(leadRequest)
                        .then((res) => {
                            if (res.statusCode === 200) {
                                localStorage.setItem('leadsApiResponse', JSON.stringify(res));
                                notify({
                                    title: 'New Lead Generated',
                                    content: `A new advisor lead has been generated.`,
                                    createdByRole: 'ADMIN',
                                    type: 'leadGeneration',
                                    action: 'View Lead',
                                    actionUrl: '/leads',
                                    unread: true
                                });
                                setLoading(false);
                                handleClose();
                                onSuccess();
                                showToast('Lead created successfully', 'success');
                            } else {
                                setLoading(false);
                                showToast('Oops! Something went wrong.', 'error');
                            }
                        })
                        .catch((err) => {
                            console.log('error', err);
                            showToast('Oops! Something went wrong.', 'error');
                            setLoading(false);
                        });
                } else {
                    setLoading(false);
                    setVerifyOtpDisabled(false);
                    showToast('Invalid OTP', 'error');
                }
            })
            .catch((err) => {
                console.log(err, '');
                showToast('Oops! Something went wrong.', 'error');
                setLoading(false);
                setVerifyOtpDisabled(false);
            });
    };

    const onSubmit = (data) => {
        if (!customerNum) {
            showToast('Please enter a valid mobile number', 'error');
            return;
        }

        let leadRequest = {
            mobileNumber: customerNum?.replace(/-/g, ''),
            ip: IpAddress,
            longitude: userCoordinates?.lng || '',
            latitude: userCoordinates?.lat || '',
            screen: 'advisor'
        };

        setLoading(true);
        setSendOtpDisabled(true);
        verifyUser(leadRequest)
            .then((res) => {
                if (res?.statusCode === 200) {
                    setLoading(false);
                    showToast(res.message, 'success');
                    setOtpSent(true);
                    // Keep the send OTP button disabled after success
                } else if (res?.statusCode === 208) {
                    leadGeneration({
                        mobileNumber: customerNum?.replace(/-/g, ''),
                        ip: IpAddress,
                        longitude: userCoordinates?.lng || '',
                        latitude: userCoordinates?.lat || '',
                        leadType: 'advisor',
                        source: 'admin'
                    })
                        .then((res) => {
                            if (res.statusCode === 200) {
                                localStorage.setItem('leadsApiResponse', JSON.stringify(res));
                                reset()
                                notify({
                                    title: 'New Lead Generated',
                                    content: `A new advisor lead has been generated.`,
                                    createdByRole: 'ADMIN',
                                    type: 'leadGeneration',
                                    action: 'View Lead',
                                    actionUrl: '/leads',
                                    unread: true
                                });
                                setLoading(false);
                                handleClose();
                                onSuccess();
                                showToast('Lead created successfully', 'success');
                            } else {
                                setLoading(false);
                                showToast('Oops! Something went wrong.', 'error');
                            }
                        })
                        .catch((err) => {
                            setLoading(false);
                            showToast('Oops! Something went wrong.', 'error');
                            console.log('error', err);
                        });
                } else {
                    setLoading(false);
                    setSendOtpDisabled(false);
                    showToast('Oops! Something went wrong.', 'error');
                }
            })
            .catch((error) => {
                setLoading(false);
                setSendOtpDisabled(false);
                showToast('Oops! Something went wrong.', 'error');
                console.log(error, 'error');
            });
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xs"
            fullWidth
            sx={{
                '& .MuiPaper-root': {
                    borderRadius: '20px',
                    padding: '20px'
                }
            }}
        >
            <Backdrop sx={{ color: '#fff', zIndex: 1000 }} open={loading}>
                <CircularProgress color="inherit" />
            </Backdrop>

            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight="bold" sx={{ color: theme.palette.primary.main }}>
                        Add New Talk to Advisor Lead
                    </Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                <Box mt={2}>
                    {!otpSent ? (
                        <Controller
                            name="mobileNumber"
                            control={control}
                            rules={{
                                required: 'Mobile number is required',
                                validate: (value) => {
                                    const digitsOnly = value?.replace(/\D/g, '');
                                    return digitsOnly?.length === 10 || 'Mobile number must be 10 digits';
                                }
                            }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    variant="outlined"
                                    fullWidth
                                    label={
                                        <>
                                            Mobile Number
                                            <span style={{ color: 'red' }}>*</span>
                                        </>
                                    }
                                    type="tel"
                                    autoFocus
                                    inputProps={{ maxLength: 12 }}
                                    onInput={(e) => {
                                        e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                        e.target.value = e.target.value.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
                                        field.onChange(e);
                                        setCustomerNum(e.target.value);
                                    }}
                                    error={!!errors.mobileNumber}
                                    helperText={errors.mobileNumber?.message}
                                    placeholder="XXX-XXX-XXXX"
                                    disabled={sendOtpDisabled}
                                />
                            )}
                        />
                    ) : (
                        <TextField
                            variant="outlined"
                            fullWidth
                            label="OTP"
                            type="tel"
                            value={otp}
                            onChange={(e) => {
                                const value = e.target.value.replace(/[^0-9]/g, '');
                                if (value.length <= 4) {
                                    setOtp(value);
                                }
                            }}
                            inputProps={{ maxLength: 4 }}
                            placeholder="Enter OTP"
                            disabled={verifyOtpDisabled}
                        />
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button
                    variant="outlined"
                    onClick={handleClose}
                    sx={{
                        borderRadius: '10px',
                        textTransform: 'none',
                        px: 3,
                        py: 1,
                        mr: 2
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={otpSent ? handleLeadGeneration : handleSubmit(onSubmit)}
                    disabled={otpSent ? verifyOtpDisabled : sendOtpDisabled}
                    sx={{
                        borderRadius: '10px',
                        textTransform: 'none',
                        px: 3,
                        py: 1,
                        background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        '&:hover': {
                            background: `linear-gradient(90deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
                        }
                    }}
                >
                    {otpSent ? 'Verify OTP' : 'Send OTP'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TalkToAdvisorLeadForm; 